import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type APlusGenerationSummary } from "reedy-data/api";
import { useApi } from "~/hooks/use-api";
import { useAnalytics } from "~/contexts/analytics-context";
import { useFeatureFlags } from "~/contexts/feature-flag-context";

export type APlusStatus = APlusGenerationSummary["status"] | "none";

export type APlusBadgeStatus = APlusStatus | "loading" | "unavailable";

export const A_PLUS_STATUS_POLL_MS = 60_000;

export const useAmplifyStatus = (productIds: string[]) => {
  const api = useApi();
  const { gaEvent } = useAnalytics();
  const { features } = useFeatureFlags();

  // Sorted so a new snapshot of the same books does not start a new request.
  const ids = [...new Set(productIds)].sort();

  const { data, isError } = useQuery({
    queryKey: ["products", ids, "amplify", "status"],
    // The event fires here so it counts each failed request once.
    queryFn: async ({ client, queryKey }) => {
      try {
        return await api.products.listAmplifySummaries(ids);
      } catch (error) {
        gaEvent({
          type: client.getQueryData(queryKey)
            ? "amplify_status_update_failed"
            : "amplify_status_failed",
          payload: { books_count: ids.length },
        });

        throw error;
      }
    },
    enabled: features.showAPlusTab === true && ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // Every attempt is logged, and a missing key does not go away on retry.
    retry: false,
    // Polling a request that never succeeded would only repeat its error.
    refetchInterval: (query) =>
      query.state.data ? A_PLUS_STATUS_POLL_MS : false,
  });

  const statusById = new Map<string, APlusStatus>(
    (data ?? []).map((summary) => [
      summary.id,
      summary.aPlusPage?.generation?.status ?? "none",
    ]),
  );

  const statusOf = (productId: string): APlusBadgeStatus => {
    // A failed refetch keeps the statuses that already loaded.
    const status = statusById.get(productId);

    if (status) {
      return status;
    }

    if (isError || !ids.includes(productId)) {
      return "unavailable";
    }

    return "loading";
  };

  return features.showAPlusTab ? statusOf : undefined;
};
